import React from "react";
import { useDispatch } from "react-redux";

import type { IGoal } from "../types";
import InputControl from "../../../components/form/InputControl";
import TextareaControl from "../../../components/form/TextareaControl";
import PrimaryModal from "../../../components/ui/PrimaryModal";
import useForm from "../../../hooks/useForm";
import getCurrentDateString from "../../../utils/getCurrentDateString";
import { addGoal, updateGoal } from "../goalsSlice";
import randomId from "../../../utils/randomId";
import DisplayErrors from "../../../components/ui/DisplayErrors";
import { ADD_GOAL, EDIT_GOAL } from "../../../utils/constants";

interface GoalFormModalProps {
  onClose: () => void;
  goal?: IGoal;
  type?: string;
}

export const GoalsFormModal: React.FC<GoalFormModalProps> = ({
  onClose,
  goal,
  type = ADD_GOAL,
}) => {
  const dispatch = useDispatch();

  const {
    values,
    error,
    errorMessage,
    validator,
    handleInputChange,
    handleSubmit,
  } = useForm({
    title: goal?.title || "",
    description: goal?.description || "",
  });

  const submitCallback = () => {
    // run validator to check if required fields are filled
    if (!validator(["title", "description"])) return;
    // if type is ADD_GOAL, then add a new goal
    if (type === ADD_GOAL) {
      // add goal if there's no error
      dispatch(
        addGoal({
          ...values,
          id: randomId(),
          favourite: false,
          completed: false,
          createdAt: getCurrentDateString(),
          updatedAt: getCurrentDateString(),
        })
      );
    }
    // if type is EDIT_GOAL, then update the goal
    else if (type === EDIT_GOAL) {
      dispatch(
        updateGoal({
          ...goal,
          ...values,
          updatedAt: getCurrentDateString(),
        })
      );
    }
    // close form modal
    onClose();
  };

  return (
    <PrimaryModal onClose={onClose}>
      <form
        onSubmit={handleSubmit(submitCallback)}
        className="flex flex-col gap-8">
        <InputControl
          id="title"
          name="title"
          defaultValue={values.title}
          placeholder="What do you want to achieve"
          onChange={handleInputChange}
        />
        <TextareaControl
          id="description"
          name="description"
          placeholder="Describe your goal"
          className="h-36"
          defaultValue={values.description}
          onChange={handleInputChange}
        />
        {error && <DisplayErrors message={errorMessage} />}
        <button type="submit" className="primary-btn text-lg">
          {/* check if action equal edit goal or add goal */}
          {type === EDIT_GOAL
            ? "save changes"
            : type === ADD_GOAL && "add new goal"}
        </button>
      </form>
    </PrimaryModal>
  );
};
